import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Connecticut Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const CTLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 0,
      cycleYears: 1,
      terminology: 'N/A',
      highlights: ['Annual renewal', 'No continuing education requirement', 'Connecticut does not mandate CE for PE licenses'],
      stateBoard: 'CT Department of Consumer Protection',
      boardUrl: 'https://portal.ct.gov/DCP/License-Services-Division/Engineer'
    },
    {
      id: 'SLP',
      name: 'Speech-Language Pathologist',
      icon: '🗣️',
      credits: 20,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['20 hours biennial', 'Qualified CE activities', 'No specific mandatory topics'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Speech-Language-Pathologist'
    },
    {
      id: 'COUNS',
      name: 'Professional Counselor',
      icon: '🧠',
      credits: 40,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['40 hours biennial', '3 hours Ethics', '1 hour Cultural Competency', '2 hours Veterans Mental Health (once per 6 years)'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Professional-Counselor'
    },
    {
      id: 'OT',
      name: 'Occupational Therapist',
      icon: '🖐️',
      credits: 24,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['24 contact hours biennial', 'Qualified continuing competency activities', 'No specific topic requirements'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Occupational-Therapist'
    },
    {
      id: 'OTA',
      name: 'Occupational Therapy Assistant',
      icon: '👐',
      credits: 18,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['18 contact hours biennial', 'Qualified continuing competency activities', 'No specific topic requirements'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Occupational-Therapist'
    },
    {
      id: 'LCSW',
      name: 'Social Worker (MSW/LCSW)',
      icon: '🤝',
      credits: 15,
      cycleYears: 1,
      terminology: 'hours',
      highlights: ['15 hours annual', 'Min 5 hours in-person or synchronous online', 'Max 10 hours asynchronous/distance learning'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Social-Worker'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 2,
      cycleYears: 1,
      terminology: 'hours',
      highlights: ['2 hours every 6 years', '1 hour PTSD/Suicide/Depression Screening', '1 hour Suicide Prevention', 'Effective 1/1/2022'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/NURBD/Registered-Nurse'
    },
    {
      id: 'LPN',
      name: 'Licensed Practical Nurse',
      icon: '💉',
      credits: 2,
      cycleYears: 1,
      terminology: 'hours',
      highlights: ['2 hours every 6 years', '1 hour PTSD/Suicide/Depression Screening', '1 hour Suicide Prevention', 'Effective 1/1/2022'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/NURBD/Licensed-Practical-Nurse'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 50,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['50 contact hours biennial', '5 hours Pharmacotherapeutics', '1 hour HIV/AIDS', '1 hour Substance Abuse', '2 hours Veterans Mental Health (once per 6 years)'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/NURBD/Advanced-Practice-Registered-Nurse'
    },
    {
      id: 'MD',
      name: 'Physician (MD/DO)',
      icon: '👨‍⚕️',
      credits: 50,
      cycleYears: 1,
      terminology: 'CME',
      highlights: ['50 contact hours per 24 months', '1 hour HIV/AIDS (once per 6 years)', '1 hour Risk Management (once per 6 years)', 'Annual renewal'],
      stateBoard: 'CT Department of Public Health',
      boardUrl: 'https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Physician'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 40,
      cycleYears: 1,
      terminology: 'CPE',
      highlights: ['40 hours annual (July 1 - June 30)', '8 hours Financial Statement Prep if attest/compilation', 'July 1 - June 30 cycle'],
      stateBoard: 'CT Department of Consumer Protection',
      boardUrl: 'https://portal.ct.gov/DCP/Occupational-and-Professional/CPA-Home-Page'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Salesperson',
      icon: '🏠',
      credits: 12,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['12 hours biennial (even years)', '3 hours mandatory topics (Fair Housing, Licensing Laws)', '9 hours elective topics'],
      stateBoard: 'CT Department of Consumer Protection',
      boardUrl: 'https://portal.ct.gov/DCP/License-Services-Division/Real-Estate-Sales'
    }
  ];

  const structuredData = [
    // Main WebPage schema
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Connecticut Professional License Renewal Requirements",
      "description": "Complete guide to Connecticut professional license renewal requirements for Engineers, Nurses, CPAs, Counselors, Therapists, and more.",
      "url": "https://cectrack.com/ct-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://portal.ct.gov/DPH",
          "https://portal.ct.gov/DCP"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Connecticut Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": prof.credits > 0 
              ? `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`
              : `${prof.name} license renewal - no continuing education required`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Connecticut License Renewal",
            "item": "https://cectrack.com/ct-license-renewal"
          }
        ]
      }
    },
    // FAQPage schema
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Connecticut professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Connecticut. Professional Counselors need 40 hours biennial, Occupational Therapists need 24 hours biennial, Social Workers need 15 hours annually, and Nurses need 2 hours every 6 years. Professional Engineers have no CE requirement."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Connecticut?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Connecticut professional licenses are overseen by the CT Department of Public Health (DPH), including Nurses, Social Workers, Counselors, and Therapists. Engineers are licensed by the CT Department of Consumer Protection (DCP)."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Connecticut professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Connecticut professions renew every 1-2 years. Engineers, Social Workers, and Nurses renew annually, while most healthcare professionals (Counselors, Therapists) renew biennially."
          }
        },
        {
          "@type": "Question",
          "name": "Does Connecticut accept online CE courses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, most Connecticut professions accept online continuing education courses from approved providers. However, some professions have limits on asynchronous or distance learning hours. Social Workers, for example, must complete a minimum of 5 hours in-person or synchronous online."
          }
        }
      ]
    },
    // GovernmentOrganization schemas
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Connecticut Department of Public Health - Practitioner Licensing",
      "url": "https://portal.ct.gov/DPH",
      "areaServed": "Connecticut",
      "description": "Licenses and regulates healthcare professionals including Nurses, Social Workers, Counselors, and Therapists in Connecticut"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Connecticut Department of Consumer Protection",
      "url": "https://portal.ct.gov/DCP",
      "areaServed": "Connecticut",
      "description": "Licenses and regulates Engineers, Real Estate professionals, and other professions in Connecticut"
    }
  ];

  return (
    <>
      <SEOHelmet
        title="Connecticut Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Connecticut professional license renewal requirements. Track CE credits for Engineers, Nurses, CPAs, Counselors, Therapists, and more."
        keywords="Connecticut license renewal, CT CE requirements, CT continuing education, Connecticut professional licenses, CT DPH, CT DCP, Connecticut nurses, CT CPAs, CT counselors"
        canonical="https://cectrack.com/ct-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #0c4a6e 0%, #0284c7 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Connecticut Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Streamline your Connecticut CE tracking. Manage all your professional license requirements in one central dashboard.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-4">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0c4a6e', marginBottom: '0.5rem' }}>12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0c4a6e', marginBottom: '0.5rem' }}>100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>CT DPH/DCP Compliant</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0c4a6e', marginBottom: '0.5rem' }}>24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Cloud Access</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <section id="professions" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Connecticut CE Requirements by Profession
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Detailed continuing education requirements for all CT licensed professionals
            </p>
          </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#dbeafe',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0c4a6e' }}>
                        {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No CE Required'}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : 'Biennial'} Renewal
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#0284c7', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Connecticut License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Connecticut professionals choose CEC Track for automated tracking, secure document storage, 
                and peace of mind knowing they'll never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Track multiple CT licenses in one dashboard',
                  'Automatic deadline calculations and reminders',
                  'Upload and categorize CE certificates',
                  'Real-time progress by category requirements',
                  'Secure cloud storage for all documents',
                  'Multi-state support for cross-border professionals'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎯</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join Connecticut professionals who trust CEC Track for hassle-free license management.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #0c4a6e 0%, #0284c7 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Connecticut State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources for Connecticut professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    CT Department of Public Health (DPH)
                  </h4>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates healthcare professionals including nurses, physicians, social workers, 
                    counselors, therapists, and speech-language pathologists.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> (860) 509-8000
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://portal.ct.gov/DPH" target="_blank" rel="noopener noreferrer">
                      portal.ct.gov/DPH
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 410 Capitol Avenue, Hartford, CT 06134
                  </div>
                  <a 
                    href="https://portal.ct.gov/DPH/Practitioner-Licensing--Investigations/PLIS/Practitioner-Licensing-and-Investigations-Section" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                  >
                    View All DPH Professions
                  </a>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    CT Department of Consumer Protection (DCP)
                  </h4>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates engineers, CPAs, real estate professionals, and other occupational licenses in Connecticut.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> (860) 713-6000
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://portal.ct.gov/DCP" target="_blank" rel="noopener noreferrer">
                      portal.ct.gov/DCP
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 165 Capitol Avenue, Hartford, CT 06106
                  </div>
                  <a 
                    href="https://portal.ct.gov/DCP/License-Services-Division/License-Services-Division" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                  >
                    License Services Division
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="CT" />

      {/* CTA Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #0c4a6e 0%, #0284c7 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Connecticut License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join Connecticut professionals who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
    </>
  );
};

export default CTLicenseRenewal;
